import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Grid,
  GridItem,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Text,
  Divider,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { getData } from '../../../utils/httpClient';
import {
  buildInstallments,
  formatDateISO,
} from '../../../utils/paymentPlan';

const CreateDictatedCourseModal = ({ isOpen, onClose, formData, setFormData, onConfirm }) => {
  const [subjects, setSubjects] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [subjectsResponse, paymentMethodsResponse] = await Promise.all([
        getData('/subjects'),
        getData('/payment-methods')
      ]);

      // El endpoint /payment-methods puede responder como array plano,
      // { payload: [...] } o { data: [...] } según la versión del back
      // (ver views/admin/paymentMethods/index.jsx que usa el mismo patrón).
      let methods = [];
      if (Array.isArray(paymentMethodsResponse)) {
        methods = paymentMethodsResponse;
      } else if (Array.isArray(paymentMethodsResponse?.payload)) {
        methods = paymentMethodsResponse.payload;
      } else if (Array.isArray(paymentMethodsResponse?.data)) {
        methods = paymentMethodsResponse.data;
      }

      // Dedupe defensivo por (name, installments). La DB ya tiene un
      // UNIQUE KEY equivalente, pero nos protegemos ante seeds viejos
      // o respuestas cacheadas que traigan repetidos.
      const seen = new Set();
      methods = methods.filter((m) => {
        const key = `${String(m?.name || '').trim().toLowerCase()}|${
          m?.installments || m?.Installments || 0
        }`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setSubjects(subjectsResponse?.payload?.subjects || []);
      setPaymentMethods(methods);

      // Precarga automática de planes de pago (Módulo 5).
      //
      // Regla de UI solicitada por el cliente: al abrir "Crear Nuevo Dictado",
      // la sección "Planes de Pago" ya debe mostrar una fila por cada plan
      // existente en el sistema (p.ej. "1 cuota", "6 cuotas", etc.). El
      // usuario sólo completa Precio total y Primera cuota, o elimina los
      // que no correspondan con el botón de papelera.
      //
      // Sólo se precarga si el formulario viene vacío (no sobrescribimos
      // datos ya cargados en modo edición / reapertura del modal con datos).
      setFormData((prev) => {
        const alreadyHasPlans =
          Array.isArray(prev?.paymentPlans) && prev.paymentPlans.length > 0;
        if (alreadyHasPlans) return prev;
        if (!methods.length) return prev;
        return {
          ...prev,
          paymentPlans: methods.map((m) => ({
            paymentMethodId: m.id,
            price: 0,
            // firstDueDate queda vacía; es requerida sólo si installments > 1
            // (ver validateForm). El cliente define esa fecha al momento de
            // crear el dictado.
            firstDueDate: '',
          })),
        };
      });
    } catch (error) {
      toast({
        title: 'Error al cargar datos',
        description: 'No se pudieron cargar los datos necesarios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, setFormData]);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, loadInitialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addPaymentPlan = () => {
    const newPlans = [...(formData.paymentPlans || [])];
    newPlans.push({
      paymentMethodId: '',
      price: 0,
      // firstDueDate: se completa al seleccionar el plan (ISO yyyy-mm-dd).
      // Las demás cuotas se calculan consecutivas mensuales (ver utils/paymentPlan).
      firstDueDate: '',
    });
    setFormData(prev => ({
      ...prev,
      paymentPlans: newPlans
    }));
  };

  const removePaymentPlan = (index) => {
    const newPlans = [...(formData.paymentPlans || [])];
    newPlans.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      paymentPlans: newPlans
    }));
  };

  const updatePaymentPlan = (index, field, value) => {
    const newPlans = [...(formData.paymentPlans || [])];
    newPlans[index] = {
      ...newPlans[index],
      [field]: field === 'price' ? parseFloat(value) || 0 : value
    };
    setFormData(prev => ({
      ...prev,
      paymentPlans: newPlans
    }));
  };

  const validateForm = () => {
    // Nota: se eliminó "price" (precio base) por requerimiento; el precio se
    // calcula a partir de los planes de pago que se agregan más abajo.
    const required = ['subject_id', 'start_date', 'end_date', 'inscription_start_date', 'inscription_end_date'];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      toast({
        title: 'Campos requeridos',
        description: `Complete los siguientes campos: ${missing.join(', ')}`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validar que al menos un plan de pago esté configurado
    if (!formData.paymentPlans || formData.paymentPlans.length === 0) {
      toast({
        title: 'Plan de pago requerido',
        description: 'Debe agregar al menos un plan de pago',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validar que todos los planes tengan método de pago y precio
    const invalidPlans = formData.paymentPlans.filter(plan => !plan.paymentMethodId || !plan.price || plan.price <= 0);
    if (invalidPlans.length > 0) {
      toast({
        title: 'Planes de pago incompletos',
        description: 'Todos los planes de pago deben tener un método de pago y un precio válido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validar "Primera cuota" para planes con más de 1 cuota.
    const missingFirstDueDate = formData.paymentPlans.some((plan) => {
      const method = paymentMethods.find(
        (m) => String(m.id) === String(plan.paymentMethodId),
      );
      const n = method?.installments || method?.Installments || 1;
      return n > 1 && !plan.firstDueDate;
    });
    if (missingFirstDueDate) {
      toast({
        title: 'Fecha de primera cuota requerida',
        description:
          'Para planes con más de 1 cuota debe indicar la fecha de vencimiento de la primera cuota.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      toast({
        title: 'Fechas inválidas',
        description: 'La fecha de inicio debe ser anterior a la fecha de fin',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (new Date(formData.inscription_start_date) >= new Date(formData.inscription_end_date)) {
      toast({
        title: 'Fechas de inscripción inválidas',
        description: 'La fecha de inicio de inscripciones debe ser anterior a la fecha de fin',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (new Date(formData.inscription_end_date) > new Date(formData.start_date)) {
      toast({
        title: 'Fechas inválidas',
        description: 'Las inscripciones deben cerrar antes del inicio del curso',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    // Enriquecer cada paymentPlan con el arreglo de cuotas calculadas.
    // NOTA importante para el backend:
    //   - Estas cuotas son CONSECUTIVAS mensuales, IGUALES en monto.
    //   - La primera cuota absorbe el residuo por redondeo.
    //   - El backend debe persistirlas (p.ej. en tabla installments o
    //     course_payment) y NO recalcularlas para evitar desincronizaciones.
    const enrichedPlans = (formData.paymentPlans || []).map((plan) => {
      const method = paymentMethods.find(
        (m) => String(m.id) === String(plan.paymentMethodId),
      );
      const n = method?.installments || method?.Installments || 1;
      const installments = buildInstallments({
        totalAmount: plan.price,
        count: n,
        firstDueDate: plan.firstDueDate,
      }).map((i) => ({
        index: i.index,
        dueDate: formatDateISO(i.dueDate),
        amount: i.amount,
      }));

      return {
        ...plan,
        installments,
      };
    });

    setFormData((prev) => ({ ...prev, paymentPlans: enrichedPlans }));
    onConfirm();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxW="90vw"
        maxH="90vh"
        bg="white"
        borderRadius="12px"
        boxShadow="xl">
        <ModalHeader fontSize="xl" fontWeight="bold" color="navy.700">
          Crear Nuevo Dictado
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH="70vh" overflowY="auto" px={6}>
          <VStack spacing={6} align="stretch">
            
            {/* Información del Dictado (antes "Materia") */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="navy.700" mb={3}>
                Información del Dictado
              </Text>
              <Divider mb={4} />
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Dictado</FormLabel>
                <Select
                  placeholder="Seleccione un dictado"
                  value={formData.subject_id || ''}
                  onChange={(e) => handleInputChange('subject_id', e.target.value)}
                  disabled={loading}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                >
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.description} - {subject.study?.description} ({subject.studyPlan?.description})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Fechas del Curso */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="navy.700" mb={3}>
                Fechas del Curso
              </Text>
              <Divider mb={4} />
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Inicio Inscripciones</FormLabel>
                    <Input
                      type="date"
                      value={formData.inscription_start_date || ''}
                      onChange={(e) => handleInputChange('inscription_start_date', e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                    />
                  </FormControl>
                </GridItem>
                
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Fin Inscripciones</FormLabel>
                    <Input
                      type="date"
                      value={formData.inscription_end_date || ''}
                      onChange={(e) => handleInputChange('inscription_end_date', e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Inicio del Curso</FormLabel>
                    <Input
                      type="date"
                      value={formData.start_date || ''}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                    />
                  </FormControl>
                </GridItem>
                
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Fin del Curso</FormLabel>
                    <Input
                      type="date"
                      value={formData.end_date || ''}
                      onChange={(e) => handleInputChange('end_date', e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/*
              Información del Curso: se eliminaron por requerimiento
              "Precio Base" (ahora se calcula por planes de pago),
              "Cupos Máximos" e "Información Adicional" (Observaciones).
              Se conserva "Cantidad de Clases" ya que NO fue solicitado eliminar.
            */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" color="navy.700" mb={3}>
                Información del Curso
              </Text>
              <Divider mb={4} />
              <Grid templateColumns="repeat(1, 1fr)" gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel fontWeight="medium">Cantidad de Clases</FormLabel>
                    <NumberInput
                      value={formData.amount_of_classes || ''}
                      onChange={(value) => handleInputChange('amount_of_classes', parseInt(value) || null)}
                      min={1}
                      bg="white"
                    >
                      <NumberInputField 
                        border="1px solid"
                        borderColor="gray.300"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* Planes de Pago */}
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="lg" fontWeight="semibold" color="navy.700">
                  Planes de Pago
                </Text>
                <Button
                  leftIcon={<AddIcon />}
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={addPaymentPlan}
                >
                  Agregar Plan
                </Button>
              </Flex>
              <Divider mb={4} />
              
              {(!formData.paymentPlans || formData.paymentPlans.length === 0) && (
                <Box p={4} border="2px dashed" borderColor="gray.300" borderRadius="md" textAlign="center">
                  <Text color="gray.500" fontSize="sm">
                    {loading
                      ? 'Cargando planes de pago...'
                      : paymentMethods.length === 0
                      ? 'No hay planes de pago configurados en el sistema.'
                      : 'Todos los planes fueron eliminados. Haga clic en "Agregar Plan" para añadir uno nuevamente.'}
                  </Text>
                </Box>
              )}

              <VStack spacing={3} align="stretch">
                {(formData.paymentPlans || []).map((plan, index) => {
                  // Plan de pago seleccionado: obtenemos cantidad de cuotas.
                  const selectedMethod = paymentMethods.find(
                    (m) => String(m.id) === String(plan.paymentMethodId),
                  );
                  const installmentsCount =
                    selectedMethod?.installments || selectedMethod?.Installments || 1;

                  // Preview de cuotas (iguales y consecutivas).
                  const previewInstallments = buildInstallments({
                    totalAmount: plan.price,
                    count: installmentsCount,
                    firstDueDate: plan.firstDueDate,
                  });

                  return (
                    <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                      <Grid templateColumns="2fr 1fr 1fr auto" gap={4} alignItems="end">
                        <GridItem>
                          <FormControl isRequired>
                            <FormLabel fontWeight="medium" fontSize="sm">Plan de Pago</FormLabel>
                            <Select
                              placeholder="Seleccione un plan de pago"
                              value={plan.paymentMethodId || ''}
                              onChange={(e) => updatePaymentPlan(index, 'paymentMethodId', e.target.value)}
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              _focus={{ borderColor: "blue.500" }}
                              size="sm"
                            >
                              {paymentMethods.map(method => {
                                const n = method.installments || method.Installments || 1;
                                return (
                                  <option key={method.id} value={method.id}>
                                    {(method.name || method.description)} - {n} cuota{n > 1 ? 's' : ''}
                                    {method.discountPercentage > 0 && ` (${method.discountPercentage}% desc.)`}
                                  </option>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl isRequired>
                            <FormLabel fontWeight="medium" fontSize="sm">Precio total</FormLabel>
                            <NumberInput
                              value={plan.price || ''}
                              onChange={(value) => updatePaymentPlan(index, 'price', value)}
                              min={0}
                              precision={2}
                              size="sm"
                              bg="white"
                            >
                              <NumberInputField
                                border="1px solid"
                                borderColor="gray.300"
                                _focus={{ borderColor: "blue.500" }}
                              />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl isRequired={installmentsCount > 1}>
                            <FormLabel fontWeight="medium" fontSize="sm">Primera cuota</FormLabel>
                            <Input
                              type="date"
                              size="sm"
                              bg="white"
                              value={plan.firstDueDate || ''}
                              onChange={(e) => updatePaymentPlan(index, 'firstDueDate', e.target.value)}
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            variant="outline"
                            size="sm"
                            onClick={() => removePaymentPlan(index)}
                            aria-label="Eliminar plan de pago"
                          />
                        </GridItem>
                      </Grid>

                      {/* Preview de cuotas: iguales y consecutivas. */}
                      {previewInstallments.length > 0 && (
                        <Box mt={3} p={3} bg="white" borderRadius="md" border="1px dashed" borderColor="gray.300">
                          <Text fontSize="sm" fontWeight="600" mb={2}>
                            {installmentsCount} {installmentsCount === 1 ? 'cuota' : 'cuotas'}: $
                            {previewInstallments[0].amount}
                            {installmentsCount > 1 && ' cada una'}
                          </Text>
                          <VStack align="stretch" spacing={1}>
                            {previewInstallments.map((inst) => (
                              <Flex key={inst.index} justify="space-between" fontSize="xs">
                                <Text>Cuota {inst.index}</Text>
                                <Text>
                                  Vto: {formatDateISO(inst.dueDate)} - ${inst.amount}
                                </Text>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </Box>

            {/* Sección "Información Adicional" eliminada por requerimiento. */}

          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.200" bg="gray.50">
          <HStack spacing={3}>
            <Button 
              variant="ghost" 
              onClick={onClose}
              size="lg"
              _hover={{ bg: "gray.100" }}
            >
              Cancelar
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit} 
              size="lg"
              px={8}
              _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
            >
              Crear Dictado
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateDictatedCourseModal;
